import React, { useState } from 'react'
import { encodeToBase64, isAcceptedFormat } from '../utils'
import { DEFAULT_FILE_SIZE } from '../constants'

/**
 * @typedef {object} ErrorResponse
 * @property {string} mssg - El mensaje de error a mostrar al usuario.
 */

/**
 * @typedef {object} FileData
 * @property {string} data - El archivo codificado en Base64.
 * @property {string} name - El nombre del archivo.
 * @property {string} type - La extensión del archivo (sin el punto).
 */

/**
 * @typedef {object} FileChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop `name`.
 * @property {FileData} target.value - Los datos del archivo subido.
 */

/**
 * @callback ChildrenRenderFunction
 * @param {object} state - El estado actual del componente.
 * @param {boolean} state.loading - Si es `true`, el archivo se está procesando.
 * @param {string | false} state.fileName - El nombre del archivo seleccionado, o `false` si no hay archivo.
 * @param {boolean} state.disabled - Si es `true`, el componente está deshabilitado.
 * @returns {React.ReactNode} El contenido a renderizar (típicamente un botón).
 */

/**
 * @typedef {object} FileUploaderAgnosticProps
 * @property {string} [idName] - Un identificador personalizado para el input de archivo. Si no se proporciona, se usa `name`.
 * @property {string} name - El nombre del campo, que se incluirá en el objeto de evento sintético del `onSuccess`.
 * @property {string[]} accept - Un array de extensiones de archivo aceptadas (ej. `['.pdf', '.jpg', '.png']`).
 * @property {function(FileChangeEvent): void} onSuccess - La función callback que se ejecuta cuando el archivo se procesa correctamente.
 * @property {function(ErrorResponse): void} onError - La función callback que se ejecuta cuando ocurre un error durante la validación o procesamiento.
 * @property {boolean} [disabled=false] - Si es `true`, el input de archivo estará deshabilitado.
 * @property {number} [maxSize=DEFAULT_FILE_SIZE] - El tamaño máximo permitido del archivo en bytes.
 * @property {ChildrenRenderFunction} children - Una función render prop que recibe el estado del componente y devuelve el UI a renderizar.
 */

/**
 * Un componente agnóstico de carga de archivos que no realiza peticiones HTTP directamente.
 * 
 * Valida el formato y tamaño del archivo, lo codifica en Base64 y devuelve los datos
 * a través del callback `onSuccess`. Es "agnóstico" porque no está acoplado a ninguna
 * API específica; el componente padre decide qué hacer con los datos del archivo.
 * 
 * Utiliza el patrón render prop a través de `children` para permitir una personalización
 * completa del UI del botón de carga.
 * 
 * @component
 * @example
 * const MyUploadForm = () => {
 *   const handleSuccess = (event) => {
 *     console.log('Archivo procesado:', event.target.value);
 *     // Aquí puedes enviar los datos a tu API
 *   };
 * 
 *   const handleError = (error) => {
 *     alert(`Error: ${error.mssg}`);
 *   };
 * 
 *   return (
 *     <FileUploaderAgnostic
 *       name="invoice"
 *       accept={['.pdf', '.jpg']}
 *       maxSize={5000000} // 5 MB
 *       onSuccess={handleSuccess}
 *       onError={handleError}
 *     >
 *       {({ loading, fileName, disabled }) => (
 *         <button disabled={loading || disabled}>
 *           {loading ? 'Procesando...' : fileName || 'Seleccionar archivo'}
 *         </button>
 *       )}
 *     </FileUploaderAgnostic>
 *   );
 * };
 * 
 * @param {FileUploaderAgnosticProps} props - Las props del componente FileUploaderAgnostic.
 */
export const FileUploaderAgnostic = ({
  idName,
  name,
  accept,
  onSuccess,
  onError,
  disabled = false,
  maxSize = DEFAULT_FILE_SIZE,
  children,
}) => {
  const [loading, setLoading] = useState(false)
  const [fileName, setFileName] = useState(false)

  const handleChangeUpload = async (e) => {
    setLoading(true)
    const file = e.target.files[0]
    if (file) {
      if (accept && accept.length > 0 && !isAcceptedFormat(file.name, accept)) {
        setLoading(false)
        return onError({ mssg: 'Archivo con formato no válido' })
      } else if (file.size > maxSize) {
        setLoading(false)
        return onError({ mssg: 'Excede el tamaño máximo' })
      } else {
        setFileName(file.name)
        const fileBase64 = await encodeToBase64(file, true)
        setLoading(false)
        return onSuccess({
          target: {
            name,
            value: { data: fileBase64, name: file.name, type: file.type.split('/')[1] },
          },
        })
      }
    }
    setLoading(false)
  }
  const nameId = `file-upload-${idName || name}`
  return (
    <div>
      <input
        id={nameId}
        name={name}
        type="file"
        style={{ display: 'none' }}
        accept={accept.join(',')}
        onChange={handleChangeUpload}
        disabled={disabled}
      />
      <label htmlFor={nameId} style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
        {children({ loading, fileName, disabled })}
      </label>
    </div>
  )
}
